// components/blog/WelcomePopup.jsx
// Appears once every 7 days for non-logged-in, non-subscribed visitors.
// Respects a minimum 60-second gap after any other popup (see popupUtils).
import { useEffect, useRef, useState } from "react";
import { X, Mail, UserPlus } from "lucide-react";
import { useAuth }              from "../../auth/AuthContext";
import { playChime }            from "../../lib/audioUtils";
import { msUntilNextPopup, recordPopupShown } from "../../lib/popupUtils";

const DISMISS_KEY = "mc_welcome_popup_dismissed";
const SUB_KEY     = "mc_newsletter_subscribed";
const TTL_MS      = 7 * 24 * 60 * 60 * 1000; // 7 days
const BASE_DELAY  = 3_500; // minimum ms before showing

const POPUP_STYLES = `
@keyframes wpSlideUp {
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
}
@keyframes wpSlideDown {
  from { opacity: 1; transform: translateY(0)    scale(1);    }
  to   { opacity: 0; transform: translateY(20px) scale(0.97); }
}
.wp-enter { animation: wpSlideUp   280ms cubic-bezier(0.34,1.56,0.64,1) forwards; }
.wp-exit  { animation: wpSlideDown 220ms ease forwards; }
@keyframes wpDot {
  0%,100% { transform: scale(1);   opacity: 0.6; }
  50%     { transform: scale(1.4); opacity: 1;   }
}
.wp-dot { animation: wpDot 2.4s ease-in-out infinite; }
.wp-dot:nth-child(2) { animation-delay: 0.3s; }
.wp-dot:nth-child(3) { animation-delay: 0.6s; }
`;

export function WelcomePopup({ onOpenSignup, onOpenNewsletter, isDark }) {
  const { user } = useAuth();
  const [phase,  setPhase]  = useState("hidden");
  const timerRef = useRef(null);

  useEffect(() => {
    if (user) return;

    const subbed    = localStorage.getItem(SUB_KEY) === "true";
    if (subbed) return;

    const raw       = localStorage.getItem(DISMISS_KEY);
    const dismissed = raw && Date.now() - parseInt(raw, 10) < TTL_MS;
    if (dismissed) return;

    // Wait at least BASE_DELAY, plus any gap owed to a recent popup.
    const delay = Math.max(BASE_DELAY, BASE_DELAY + msUntilNextPopup());

    function firePopup() {
      recordPopupShown();
      localStorage.setItem(DISMISS_KEY, Date.now().toString()); // prevent re-firing on next navigation
      setPhase("entering");
      playChime();
      setTimeout(() => setPhase("visible"), 30);
    }

    timerRef.current = setTimeout(() => {
      // Re-check gap in case SupportPopup fired while we were waiting
      const remaining = msUntilNextPopup();
      if (remaining > 0) {
        timerRef.current = setTimeout(firePopup, remaining + 500);
        return;
      }
      firePopup();
    }, delay);

    return () => clearTimeout(timerRef.current);
  }, [user]);

  function dismiss() {
    setPhase("exiting");
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setTimeout(() => setPhase("hidden"), 240);
  }

  function handleSignup() {
    dismiss();
    onOpenSignup?.();
  }

  function handleNewsletter() {
    dismiss();
    onOpenNewsletter?.();
  }

  if (phase === "hidden") return null;

  const animClass = phase === "exiting" ? "wp-exit" : "wp-enter";
  const bg  = isDark ? "#111114" : "#EDEBE5";
  const bdr = isDark ? "#27272F" : "#C8C4BC";

  return (
    <>
      <style>{POPUP_STYLES}</style>
      <div
        className={`blog-theme ${animClass}`}
        data-blog-theme={isDark ? "dark" : "light"}
        style={{
          position:     "fixed",
          bottom:       90,
          right:        20,
          zIndex:       9800,
          width:        320,
          background:   bg,
          border:       `0.5px solid ${bdr}`,
          borderRadius: 14,
          boxShadow:    isDark
            ? "0 24px 60px rgba(0,0,0,0.7), 0 4px 16px rgba(0,0,0,0.4)"
            : "0 24px 60px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.07)",
          overflow:     "hidden",
          fontFamily:   "'Inter', system-ui, sans-serif",
        }}
      >
        {/* Teal accent strip */}
        <div style={{ height: 3, background: "linear-gradient(90deg, var(--blog-accent-teal), #00e5cc)" }} />

        <div style={{ padding: "18px 20px 20px" }}>
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                {[0,1,2].map(i => (
                  <span key={i} className="wp-dot" style={{
                    display:      "block",
                    width:        6,
                    height:       6,
                    borderRadius: "50%",
                    background:   "var(--blog-accent-teal)",
                  }} />
                ))}
              </div>
              <div
                className="font-display font-medium"
                style={{ fontSize: 17, lineHeight: 1.2, color: "var(--blog-text-primary)", letterSpacing: "-0.01em" }}
              >
                Hey, glad you're here.
              </div>
            </div>
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              style={{
                background: "transparent",
                border:     "none",
                color:      "var(--blog-text-tertiary)",
                cursor:     "pointer",
                padding:    4,
                marginTop:  -2,
                lineHeight: 1,
              }}
            >
              <X style={{ width: 15, height: 15 }} />
            </button>
          </div>

          <p style={{ fontSize: 12, lineHeight: 1.6, color: "var(--blog-text-secondary)", margin: "0 0 16px" }}>
            If you like what you're reading, I'd love to have you. New posts go out when they're ready, not on a schedule.{" "}
            <span style={{ color: "var(--blog-accent-teal)", fontWeight: 600 }}>Free either way.</span>
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              onClick={handleSignup}
              style={{
                display:       "flex",
                alignItems:    "center",
                justifyContent:"center",
                gap:           8,
                padding:       "11px 16px",
                borderRadius:  8,
                border:        "none",
                background:    "var(--blog-text-primary)",
                color:         "var(--blog-bg-primary)",
                fontSize:      12,
                fontWeight:    500,
                letterSpacing: "0.03em",
                cursor:        "pointer",
                fontFamily:    "inherit",
              }}
            >
              <UserPlus style={{ width: 13, height: 13 }} />
              Create free account
            </button>
            <button
              onClick={handleNewsletter}
              style={{
                display:       "flex",
                alignItems:    "center",
                justifyContent:"center",
                gap:           8,
                padding:       "10px 16px",
                borderRadius:  8,
                border:        "0.5px solid var(--blog-border-primary)",
                background:    "transparent",
                color:         "var(--blog-text-secondary)",
                fontSize:      12,
                fontWeight:    500,
                letterSpacing: "0.03em",
                cursor:        "pointer",
                fontFamily:    "inherit",
              }}
            >
              <Mail style={{ width: 13, height: 13 }} />
              Newsletter only
            </button>
          </div>

          <p
            onClick={dismiss}
            style={{
              marginTop:  12,
              fontSize:   10,
              textAlign:  "center",
              color:      "var(--blog-text-tertiary)",
              cursor:     "pointer",
              letterSpacing: "0.02em",
            }}
          >
            Maybe later
          </p>
        </div>
      </div>
    </>
  );
}
